export const getOdataDownloadUrl = (dataSource, query) => `export/${dataSource}/csv${query}`;

export function odataToGeoJson(data, latField, lonField) {
  if (!latField || !lonField || !data || !Array.isArray(data)) {
    return null;
  }

  const geojson = {
    type: "FeatureCollection",
    features: []
  };

  data.forEach(item => {
    const latValue = item[latField];
    const lonValue = item[lonField];
    if (latValue === undefined || latValue === null || lonValue === undefined || lonValue === null) return; // skip invalid rows

    const lat = parseFloat(String(latValue).replace(",", "."));
    const lon = parseFloat(String(lonValue).replace(",", "."));

    if (isNaN(lat) || isNaN(lon)) return; // skip invalid rows

    const feature = {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [lon, lat]
      },
      properties: {}
    };

    // copy all properties except lat/lon
    for (const key in item) {
      if (key !== latField && key !== lonField) {
        feature.properties[key] = item[key];
      }
    }

    geojson.features.push(feature);
  });

  return geojson;
}

// Function that checks whether two OData queries are different.
// You can ignore query parameters by passing them as a list of strings in ignoreParams.
export const isQueryDifferent = (currentQuery, prevQuery, ignoreParams = []) => {
  if (prevQuery === undefined || prevQuery === null) return true;
  const currentQueryParams = new URLSearchParams(currentQuery);
  const prevQueryParams = new URLSearchParams(prevQuery);
  ignoreParams.forEach(param => {
    currentQueryParams.delete(param);
    prevQueryParams.delete(param);
  });
  return currentQueryParams.toString() !== prevQueryParams.toString();
};

export const getQueryParamsFromTable = (pagination, sorting, columnFilters, globalFilter, columns) => {
  const top = pagination ? pagination.pageSize : null;
  const skip = pagination ? pagination.pageIndex * pagination.pageSize : null;
  const orderBy = (sorting || []).map(({id, desc}) => `${id} ${desc ? "desc" : "asc"}`);
  const search = globalFilter;
  const filter = (columnFilters || []).map(({id, value}) => {
    const isNumeric = (columns || []).find(column => column.id === id).isNumeric;
    const isEqualMode = value[0] === '"' && value[value.length - 1] === '"';
    return isNumeric
      ? {[id]: {eq: parseFloat(value)}}
      : isEqualMode
        ? {[`tolower(${id})`]: {eq: value.slice(1, -1).toLowerCase()}}
        : {[`tolower(${id})`]: {contains: value.toLowerCase()}};
  });
  return {
    top: top,
    skip: skip,
    orderBy: orderBy,
    search: search,
    filter: filter
  };
};

// Function that builds a valid queryParams object from the input parameters,
// ready to be passed to buildQuery to generate a valid OData query.
export const getOdataQueryParams = (groupBy, select, filter) => {
  const queryParams = {};
  const transformParams = [];
  if (filter) {
    const hasNullValues = Object.values(filter).some(f => (f.in || []).includes(null));
    if (hasNullValues) {
      const transformedFilter = {
        and: Object.entries(filter).map(([field, condition]) => {
          const values = condition.in ?? [];

          const nonNullValues = values.filter(v => v !== null);
          const hasNull = values.includes(null);

          const orConditions = [];

          if (nonNullValues.length) {
            orConditions.push({[field]: {in: nonNullValues}});
          }

          if (hasNull) {
            orConditions.push({[field]: {eq: null}});
          }

          return {or: orConditions};
        })
      };
      transformParams.push({filter: transformedFilter});
    } else {
      transformParams.push({filter: filter});
    }
  }
  if (groupBy) {
    transformParams.push({groupBy: groupBy});
  }
  if (transformParams.length > 0) {
    queryParams.transform = transformParams;
  }
  if (select) {
    queryParams.select = select;
  }

  return Object.keys(queryParams).length > 0 ? queryParams : null;
};

// Function that reads the keyDim value (SEQ) from metadata.
export const getKeyDim = metadata => (metadata ? metadata?.["@DataPortal.Seq"] : null);

// Function that checks whether the OData service is internal or external.
export const getIsInternalService = metadata => (metadata ? Boolean(metadata?.["@DataPortal.Seq"]) : false);
